import PropTypes from 'prop-types';

import {
  Wrapper,
  Button,
  ButtonIconWrapper,
  ButtonLabelWrapper,
  Popover,
  List,
  Item,
  ItemIconWrapper,
  ItemLabelWrapper,
  Link,
} from './index.styles';

import Icon from '../icon';

function MenuItem({ as, iconHeight, iconSrc, iconWidth, label, variant, ...otherProps }) {
  const Component = otherProps.href || otherProps.to ? Link : Item;

  return (
    <Component {...otherProps} as={as} variant={variant}>
      {iconSrc && (
        <ItemIconWrapper variant={variant}>
          <Icon alt="" src={iconSrc} width={iconWidth} height={iconHeight} inline />
        </ItemIconWrapper>
      )}
      <ItemLabelWrapper>{label}</ItemLabelWrapper>
    </Component>
  );
}

MenuItem.propTypes = {
  as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  href: PropTypes.string,
  iconHeight: PropTypes.number,
  iconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  iconWidth: PropTypes.number,
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  to: PropTypes.string,
  variant: PropTypes.oneOf(['destructive']),
};

/**
 * Renders a menu. Supports icons both in button and items.
 */
export default function Menu({
  disabled,
  hasLabel,
  label,
  leftIconHeight,
  leftIconSrc,
  leftIconWidth,
  rightIconHeight,
  rightIconSrc,
  rightIconWidth,
  items,
  size = 'regular',
  title,
  variant = 'secondary',
}) {
  return (
    <Wrapper>
      <Button
        disabled={disabled}
        hasLabel={hasLabel ?? Boolean(label)}
        size={size}
        title={title}
        variant={variant}
      >
        {leftIconSrc && (
          <ButtonIconWrapper>
            <Icon alt="" src={leftIconSrc} width={leftIconWidth} height={leftIconHeight} inline />
          </ButtonIconWrapper>
        )}
        {label && <ButtonLabelWrapper>{label}</ButtonLabelWrapper>}
        {rightIconSrc && (
          <ButtonIconWrapper>
            <Icon
              alt=""
              src={rightIconSrc}
              width={rightIconWidth}
              height={rightIconHeight}
              inline
            />
          </ButtonIconWrapper>
        )}
      </Button>
      <Popover>
        <List>
          {items &&
            items.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <MenuItem key={index} {...item} />
            ))}
        </List>
      </Popover>
    </Wrapper>
  );
}

Menu.propTypes = {
  /**
   * Whether to disable the menu.
   */
  disabled: PropTypes.bool,
  /**
   * Whether the menu has a (text-based) label. Usually determined by the presence of `label`.
   */
  hasLabel: PropTypes.bool,
  /**
   * Menu items.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
      href: PropTypes.string,
      iconHeight: PropTypes.number,
      iconSrc: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.node,
        PropTypes.object,
        PropTypes.string,
      ]),
      iconWidth: PropTypes.number,
      label: PropTypes.string.isRequired,
      onSelect: PropTypes.func,
      to: PropTypes.string,
      variant: PropTypes.oneOf(['destructive']),
    }),
  ).isRequired,
  /**
   * Menu button label.
   */
  label: PropTypes.node,
  /**
   * Left icon height in pixels (recommended if icon URL is used).
   */
  leftIconHeight: PropTypes.number,
  /**
   * Left icon, left icon source or left icon URL.
   */
  leftIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Left icon width in pixels (recommended if icon URL is used).
   */
  leftIconWidth: PropTypes.number,
  /**
   * Right icon height in pixels (recommended if icon URL is used).
   */
  rightIconHeight: PropTypes.number,
  /**
   * Right icon, right icon source or right icon URL.
   */
  rightIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Right icon width in pixels (recommended if icon URL is used).
   */
  rightIconWidth: PropTypes.number,
  /**
   * Menu button size.
   */
  size: PropTypes.oneOf(['regular', 'small']),
  /**
   * Menu button title.
   */
  title: PropTypes.string,
  /**
   * Menu button variant.
   */
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
};
